import { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useGradient } from "../contexts/GradientContext";
import apiService from "../services/api";
import { fetchWeatherAndSetBackground } from "../utils/weatherBackground";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isUSA, setIsUSA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [weatherType, setWeatherType] = useState("");
  const [weatherIcon, setWeatherIcon] = useState("");
  const [temperature, setTemperature] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState("");

  const navigate = useNavigate();
  const { currentGradient } = useGradient();

  const preloadAndSetBackground = (url) => {
    if (!url) return;
    const img = new Image();

    img.onload = () => setBackgroundImage(url);
    img.onerror = () => {};
    img.src = url;
  };

  useEffect(() => {
    let mounted = true;
    const hasNavigator = typeof navigator !== "undefined";

    (async () => {
      await fetchWeatherAndSetBackground({
        setTemperature: (t) => mounted && setTemperature(t),
        setWeatherIcon: (i) => mounted && setWeatherIcon(i),
        setWeatherType: (s) => mounted && setWeatherType(s),
        setIsUSA: (b) => mounted && setIsUSA(b),
        setBackgroundUrl: (url) => mounted && preloadAndSetBackground(url),
        geolocator: hasNavigator ? navigator : undefined,
      });
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.forgotPassword(email);

      if (response.status === 200) {
        toast.success(
          "If an account exists with this email, you will receive a password reset link."
        );
        setEmail("");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.success(
          "If an account exists with this email, you will receive a password reset link."
        );
        setEmail("");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (error) {
      toast.success(
        "If an account exists with this email, you will receive a password reset link."
      );
      setEmail("");
      setTimeout(() => navigate("/login"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => window.location.replace("/login");

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background: `linear-gradient(rgba(31, 41, 55, 0.5), rgba(31, 41, 55, 0.5)), ${currentGradient}`,
      }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-5xl"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        {/* Main Card Container */}
        <div
          className="relative backdrop-blur-sm bg-gray-800/20 rounded-3xl overflow-hidden"
          style={{
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 0, 0, 0.3)",
            minHeight: "650px",
          }}
        >
          {/* Background Image - Full width */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              filter: "brightness(0.9)",
            }}
          />

          {/* Gradient Overlay - from left to right */}
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
                "linear-gradient(to right, rgba(31, 41, 55, 0.98), rgba(31, 41, 55, 0.9) 30%, rgba(31, 41, 55, 0.7) 50%, rgba(31, 41, 55, 0.3) 70%, transparent 90%)",
            }}
          />

          {/* Content Container */}
          <div className="relative flex z-20">
            {/* Left Side - Forgot Password Form */}
            <div
              className="relative px-6 py-12 sm:p-12 flex flex-col justify-center"
              style={{ width: "480px", minHeight: "650px" }}
            >
              {/* Logo - positioned higher and to the left */}
              <div className="absolute top-8 left-6 sm:left-10">
                <div
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-10 h-10"
                >
                  {!logoError ? (
                    <img
                      alt="Lunova Player Logo"
                      className="w-full h-full object-contain"
                      src="/logo.png"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg" />
                  )}
                </div>
              </div>

              {/* Form Content */}
              <div className="mt-16 w-full max-w-sm">
                <div className="mb-8">
                  <p
                    className="text-gray-400 text-xs uppercase tracking-wider mb-4"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Reset your password
                  </p>
                  <h2
                    className="text-3xl font-bold text-white mb-3 whitespace-nowrap"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Forgot password?
                  </h2>
                  <p
                    className="text-gray-400"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Enter your email address and we'll send you a link to reset
                    your password.
                  </p>
                </div>

                {/* Forgot Password Form */}
                <form
                  aria-label="Password reset form"
                  className="space-y-5"
                  onSubmit={handleSubmit}
                >
                  {/* Email Field */}
                  <div className="relative">
                    <input
                      required
                      aria-describedby="reset-description"
                      aria-label="Email address for password reset"
                      aria-required="true"
                      autoComplete="email"
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      id="email-input"
                      name="email"
                      placeholder="Email"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <i className="fas fa-envelope absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <span className="sr-only" id="reset-description">
                      Enter your email to receive a password reset link
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <button
                      aria-label="Cancel and return to login"
                      className="w-full py-3 px-6 rounded-full bg-gray-600/50 text-gray-300 text-center font-semibold hover:bg-gray-600/60 active:scale-95 transition-all duration-200 order-2 sm:order-1"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="button"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      aria-busy={loading}
                      aria-label="Submit"
                      className="w-full py-3 px-6 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center"
                      disabled={loading}
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="submit"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>

                  {/* Already registered */}
                  <div className="text-center pt-4">
                    <p
                      className="text-gray-400"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      Remembered your password?{" "}
                      <button
                        aria-label="Return to sign in page"
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        type="button"
                        onClick={() => window.location.replace("/login")}
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Side - Empty space for image to show through */}
            <div className="flex-1" />
          </div>

          {/* Weather Info Overlays */}
          {temperature !== null && (
            <>
              {/* Temperature in top right */}
              <div className="absolute top-8 right-12 text-right">
                <div className="flex items-center space-x-2">
                  <i
                    className={`fas ${weatherIcon} text-xl text-white drop-shadow-lg`}
                  />
                  <p
                    className="text-2xl font-bold text-white drop-shadow-lg"
                    style={{ fontFamily: "Open Sans, sans-serif" }}
                  >
                    {temperature}°{isUSA ? "F" : "C"}
                  </p>
                </div>
              </div>

              {/* Weather description in bottom right */}
              <div className="absolute bottom-8 right-12 text-right">
                <p
                  className="text-white text-xs font-semibold drop-shadow-lg"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                >
                  <i className={`fas ${weatherIcon} mr-1 text-xs`} />
                  {weatherType}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <p
            className="text-xs text-gray-400"
            style={{ fontFamily: "Open Sans, sans-serif", fontWeight: 400 }}
          >
            By using this service, you agree to our{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/tos"
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
